import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Skeleton } from '@swyftx/aviary/atoms/Skeleton';
import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { LendingHand } from '@swyftx/aviary/icons/outlined';
import { CustomTooltip } from '@swyftx/features/loans/components/SingleLoansDashboard/CustomTooltip';
import { useSingleLoanSummaryCard } from '@swyftx/features/loans/components/SingleLoansDashboard/api/hooks/useSingleLoanSummaryCard';

import { Loan } from '@shared/api/@types/loans';

import { useFetchLoanAgreements } from '../../hooks/useFetchLoanAgreements';

type Props = {
  className?: string;
  isFetchingLoan?: boolean;
  loanData?: Loan;
};

const LoanSummaryCard: React.FC<Props> = ({ isFetchingLoan, loanData }) => {
  const { t } = useTranslation('loans');
  const isXs = useTailwindBreakpoint('xs');
  const isSm = useTailwindBreakpoint('sm');

  const {
    loanId,
    loanTerm,
    loanTermDates,
    loanType,
    originationFee,
    totalOutstandingBalance,
    outstandingPrincipal,
    principal,
    totalInterestAccrued,
    interestRate,
    comparisonRate,
  } = useSingleLoanSummaryCard(loanData);

  const { data: loanAgreementsData } = useFetchLoanAgreements(loanId ?? '');
  const agreementLink = loanAgreementsData?.[0]?.link;

  const rowItem = (type: string, value?: string): JSX.Element => (
    <div className='flex w-full flex-row items-center justify-between gap-4'>
      <Skeleton loading={isFetchingLoan}>
        <CustomTooltip
          title={t(`singleLoan.loanSummaryCard.${type}.title` as unknown as TemplateStringsArray)}
          tooltip={t(`singleLoan.loanSummaryCard.${type}.tooltip` as unknown as TemplateStringsArray)}
        />
      </Skeleton>

      <Skeleton loading={isFetchingLoan}>
        <div className='min-w-0 text-right'>
          <Numeric size='medium' weight='emphasis' className='break-words'>
            {value ?? '-'}
          </Numeric>
        </div>
      </Skeleton>
    </div>
  );

  return (
    <Card>
      <CardHeader className='align-center flex-row justify-between'>
        <Skeleton loading={isFetchingLoan} className='h-24 w-24 rounded-full'>
          <FlexLayout alignItems='center' spacing={8}>
            <LendingHand className='h-20 w-20' />
            <CardTitle className='flex gap-2 text-lg'>{t('singleLoan.loanSummaryCard.title')}</CardTitle>
          </FlexLayout>
        </Skeleton>
      </CardHeader>

      <CardContent>
        <div className='mb-16 mt-24 space-y-12 md:mb-24'>
          {rowItem('loanType', loanType)}
          {rowItem('loanId', loanId)}
          {rowItem('principal', principal)}
          {rowItem('interestRate', interestRate)}
          {rowItem('comparisonRate', comparisonRate)}
          {rowItem('loanTerm', loanTerm)}
          {rowItem('loanTermDates', loanTermDates)}
          {rowItem('outstandingPrincipal', outstandingPrincipal)}
          {rowItem('originationFee', originationFee)}
          {rowItem('totalInterestAccrued', totalInterestAccrued)}
          {rowItem('totalOutstandingBalance', totalOutstandingBalance)}
        </div>

        <div className='pt-12 md:pt-16'>
          <Skeleton loading={isFetchingLoan} className='w-full'>
            <Button
              variant='filled'
              className='w-full md:w-auto'
              size={isSm || isXs ? 'md' : 'lg'}
              // TODO: confirm actual disabled check
              disabled={!agreementLink}
              onClick={() => {
                if (agreementLink) {
                  window.open(agreementLink, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              {t('singleLoan.loanSummaryCard.downloadAgreement')}
            </Button>
          </Skeleton>
        </div>
      </CardContent>
    </Card>
  );
};

LoanSummaryCard.displayName = 'LoanSummaryCard';

export { LoanSummaryCard };
